import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, BookOpen, DollarSign, Trash2, Loader2 } from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get(`/admin/users/${userId}`);
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  const handleResetPassword = async () => {
    setResetLoading(true);
    try {
      const payload = newPassword.trim() ? { new_password: newPassword.trim() } : {};
      const response = await api.put(`/admin/users/${userId}/reset-password`, payload);
      const generated = response.data?.temporary_password || '';
      setTempPassword(generated);
      setNewPassword('');
      if (generated) {
        alert(`Contrasena temporal generada: ${generated}`);
      } else {
        alert('Contrasena actualizada correctamente');
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al restablecer contrasena');
    } finally {
      setResetLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`¿Eliminar permanentemente a ${user.name || user.email}?\n\nEsto eliminará:\n- El usuario\n- Sus inscripciones\n- Sus pagos\n- Su contacto en CRM\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(`Usuario ${user.name || user.email} eliminado`);
      navigate('/admin/alumnos');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-[hsl(var(--sage-light))]">
        <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-[hsl(var(--sage-light))]">
        <p className="text-[hsl(var(--text-secondary))]">Usuario no encontrado</p>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/alumnos"
              className="p-2 rounded-full hover:bg-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-[hsl(var(--text-secondary))]" strokeWidth={1.5} />
            </Link>
            <h1 className="text-3xl font-heading text-[hsl(var(--text-primary))]">
              Detalle de alumno
            </h1>
          </div>
          <Button
            variant="outline"
            onClick={handleDeleteUser}
            disabled={deleting}
            className="text-red-500 border-red-200 hover:bg-red-50 hover:border-red-300"
          >
            {deleting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Eliminando...</>
            ) : (
              <><Trash2 className="w-4 h-4 mr-2" /> Eliminar alumno</>
            )}
          </Button>
        </div>

        <div className="card-soft mb-8">
          <div className="flex items-center gap-6 mb-6">
            <div className="w-20 h-20 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center">
              <span className="text-[hsl(var(--accent))] font-heading text-3xl">
                {user.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))]">
                {user.name}
              </h2>
              <p className="text-[hsl(var(--text-secondary))] flex items-center gap-2">
                <Mail className="w-4 h-4" /> {user.email}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[hsl(var(--sage-light))] rounded-xl">
            <div className="text-center">
              <p className="text-sm text-[hsl(var(--text-tertiary))]">Registrado</p>
              <p className="font-medium text-[hsl(var(--text-primary))]">
                {user.created_at ? new Date(user.created_at).toLocaleDateString('es-ES') : '-'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-[hsl(var(--text-tertiary))]">Cursos</p>
              <p className="font-medium text-[hsl(var(--sage))]">{user.enrollments?.length || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-[hsl(var(--text-tertiary))]">Pagos</p>
              <p className="font-medium text-[hsl(var(--accent))]">{user.payments?.length || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-[hsl(var(--text-tertiary))]">Onboarding</p>
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                user.onboarding_completed
                  ? 'bg-[hsl(var(--sage))]/20 text-[hsl(var(--sage))]'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {user.onboarding_completed ? 'Completado' : 'Pendiente'}
              </span>
            </div>
          </div>
        </div>

        <div className="card-soft mb-8">
          <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-4">
            Seguridad de acceso
          </h3>
          <p className="text-sm text-[hsl(var(--text-secondary))] mb-4">
            Puedes definir una contrasena manual o dejarlo vacio para generar una temporal automatica.
          </p>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Nueva contrasena (opcional)"
              className="flex-1 border rounded-lg px-3 py-2"
            />
            <Button
              onClick={handleResetPassword}
              disabled={resetLoading}
              className="btn-sage"
            >
              {resetLoading ? 'Restableciendo...' : 'Restablecer contrasena'}
            </Button>
          </div>
          {tempPassword && (
            <p className="text-sm text-amber-700 mt-3">
              Ultima contrasena temporal generada: <strong>{tempPassword}</strong>
            </p>
          )}
        </div>

        <div className="card-soft mb-8">
          <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-6 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[hsl(var(--sage))]" strokeWidth={1.5} />
            Cursos inscritos
          </h3>

          {user.enrollments?.length > 0 ? (
            <div className="space-y-4">
              {user.enrollments.map((enrollment) => {
                const isLive = enrollment.course_type === 'live';
                const isInstallment = enrollment.payment_type === 'subscription' || enrollment.payment_type === 'installment' || enrollment.payment_type === 'cuotas' || (enrollment.installments_total || 0) > 1;
                const installmentsTotal = enrollment.installments_total || 1;
                const installmentsPaid = enrollment.installments_paid || 0;
                const totalPrice = enrollment.total_price || enrollment.total_amount || 0;
                return (
                  <div key={enrollment.id} className="p-4 border border-[hsl(var(--border))] rounded-xl" data-testid={`enrollment-row-${enrollment.id}`}>
                    <div className="flex items-center justify-between mb-3 gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-[hsl(var(--text-primary))]">
                          {enrollment.course?.title || 'Curso'}
                        </h4>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${isLive ? 'bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]' : 'bg-blue-50 text-blue-700'}`}>
                          {isLive ? 'En vivo' : 'Grabado'}
                        </span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        enrollment.status === 'completed'
                          ? 'bg-[hsl(var(--sage))]/20 text-[hsl(var(--sage))]'
                          : 'bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                      }`}>
                        {enrollment.status === 'completed' ? 'Completado' : enrollment.status === 'pending' ? 'Pendiente' : 'Activo'}
                      </span>
                    </div>
                    {isLive ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                        <div>
                          <p className="text-[hsl(var(--text-tertiary))]">Total</p>
                          <p className="font-medium text-[hsl(var(--text-primary))]">{totalPrice}€</p>
                        </div>
                        <div>
                          <p className="text-[hsl(var(--text-tertiary))]">Modalidad</p>
                          <p className="font-medium text-[hsl(var(--text-primary))]">
                            {isInstallment ? `${installmentsTotal} cuotas` : 'Pago único'}
                          </p>
                        </div>
                        <div>
                          <p className="text-[hsl(var(--text-tertiary))]">Pagos</p>
                          <p className="font-medium text-[hsl(var(--text-primary))]">
                            {isInstallment
                              ? `${installmentsPaid} / ${installmentsTotal}`
                              : (installmentsPaid >= 1 ? 'Pagado' : 'Pendiente')}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[hsl(var(--text-tertiary))]">Progreso</span>
                          <span className="font-medium">{enrollment.progress || 0}%</span>
                        </div>
                        <Progress value={enrollment.progress || 0} className="h-2" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[hsl(var(--text-tertiary))] text-center py-6">
              No tiene cursos inscritos
            </p>
          )}
        </div>

        <div className="card-soft">
          <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-6 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
            Historial de pagos
          </h3>

          {user.payments?.length > 0 ? (
            <div className="space-y-3">
              {user.payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-4 bg-[hsl(var(--sage-light))] rounded-xl">
                  <div>
                    <p className="font-medium text-[hsl(var(--text-primary))]">{payment.amount}EUR</p>
                    <p className="text-sm text-[hsl(var(--text-tertiary))]">
                      {payment.created_at ? new Date(payment.created_at).toLocaleDateString('es-ES') : '-'}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    payment.status === 'completed'
                      ? 'bg-[hsl(var(--sage))]/20 text-[hsl(var(--sage))]'
                      : payment.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-600'
                      : 'bg-red-100 text-red-500'
                  }`}>
                    {payment.status === 'completed' ? 'Completado' : payment.status === 'pending' ? 'Pendiente' : 'Fallido'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[hsl(var(--text-tertiary))] text-center py-6">
              No tiene pagos registrados
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetail;
